import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface ApiToken {
  id: string;
  name: string;
  description?: string;
  token: string;
  scopes: string[];
  created_at: string;
  expires_at: string;
}

interface NewToken {
  name: string;
  description?: string;
  expiration_days: number;
  scopes: string[];
}

export function useApiTokens() {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTokens = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('api_tokens')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTokens(data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar tokens:', err);
      setError('Não foi possível carregar os tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();

    // Inscreve para atualizações em tempo real
    const channel = supabase
      .channel('api_tokens_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'api_tokens'
      }, () => {
        fetchTokens();
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchTokens]);

  const createToken = async (tokenData: NewToken) => {
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + tokenData.expiration_days);

      const { data, error } = await supabase
        .from('api_tokens')
        .insert([{
          ...tokenData,
          token: `nsp_${crypto.randomUUID().replace(/-/g, '')}`,
          expires_at: expiresAt.toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      setTokens(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Erro ao criar token:', err);
      throw new Error('Não foi possível criar o token');
    }
  };

  const deleteToken = async (id: string) => {
    try {
      const { error } = await supabase
        .from('api_tokens')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setTokens(prev => prev.filter(token => token.id !== id));
    } catch (err) {
      console.error('Erro ao deletar token:', err);
      throw new Error('Não foi possível deletar o token');
    }
  };

  const refreshToken = async (id: string) => {
    try {
      const token = tokens.find(t => t.id === id);
      if (!token) throw new Error('Token não encontrado');

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30); // Renova por mais 30 dias

      const { data, error } = await supabase
        .from('api_tokens')
        .update({
          token: `nsp_${crypto.randomUUID().replace(/-/g, '')}`,
          expires_at: expiresAt.toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setTokens(prev => prev.map(t => t.id === id ? data : t));
      return data;
    } catch (err) {
      console.error('Erro ao renovar token:', err);
      throw new Error('Não foi possível renovar o token');
    }
  };

  return {
    tokens,
    loading,
    error,
    createToken,
    deleteToken,
    refreshToken,
    refresh: fetchTokens
  };
}